import { Box, Chip, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../Card/Card";
import Title from "../Title/Title";
import useStyles from "./styles";
import { ReactComponent as EmptyIcon } from "../../../assets/images/empty-search.svg";
import { useTranslation } from 'react-i18next';
import { resizedImage } from "../../../utils/resizedImage";
import { C } from "../../../utils/designTokens";

// ── Standardized category icons ─────────────────────────────────────────
// Map common category names to MUI emoji-style icons so chips look
// consistent across restaurants (DoorDash / UberEats pattern).
import LocalPizzaIcon from "@mui/icons-material/LocalPizza";
import LunchDiningIcon from "@mui/icons-material/LunchDining";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import RamenDiningIcon from "@mui/icons-material/RamenDining";
import RiceBowlIcon from "@mui/icons-material/RiceBowl";
import SetMealIcon from "@mui/icons-material/SetMeal";
import KebabDiningIcon from "@mui/icons-material/KebabDining";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import LocalDrinkIcon from "@mui/icons-material/LocalDrink";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import IcecreamIcon from "@mui/icons-material/Icecream";
import CakeIcon from "@mui/icons-material/Cake";
import CookieIcon from "@mui/icons-material/Cookie";
import BakeryDiningIcon from "@mui/icons-material/BakeryDining";
import EggAltIcon from "@mui/icons-material/EggAlt";
import BreakfastDiningIcon from "@mui/icons-material/BreakfastDining";
import TapasIcon from "@mui/icons-material/Tapas";
import SoupKitchenIcon from "@mui/icons-material/SoupKitchen";
import LiquorIcon from "@mui/icons-material/Liquor";
import EmojiFoodBeverageIcon from "@mui/icons-material/EmojiFoodBeverage";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  CATERING_BORDER,
  CATERING_COLOR,
  CATERING_SOFT,
  queryMentionsCatering,
} from "../../../utils/catering";

// Lookup table: lowercase keyword → MUI icon component. First match wins,
// so ORDER MATTERS — put more specific rules above broader ones.
const CATEGORY_ICON_RULES = [
  // Grilled wins over flatbread / pizza when both appear (e.g. "Grilled flatbread")
  [/(^|\s)(grill|grilled|bbq|barbecue|tandoor|kebab|kabob|skewer|tikka)/i, KebabDiningIcon],
  [/(^|\s)(pizza)/i, LocalPizzaIcon],
  [/(^|\s)(flatbread|naan|pita|roti|paratha|bread|bagel|toast|loaf|focaccia)/i, BakeryDiningIcon],
  [/(^|\s)(burger|sandwich|sliders?)/i, LunchDiningIcon],
  [/(^|\s)(pasta|noodle|spaghetti|ramen)/i, RamenDiningIcon],
  [/(^|\s)(rice|biryani|bowl|curry|stir.?fry)/i, RiceBowlIcon],
  [/(^|\s)(sushi|sashimi|seafood|fish|salmon|shrimp|mussels?|crab|lobster)/i, SetMealIcon],
  [/(^|\s)(coffee|espresso|latte|cappuccino|chai|tea)/i, LocalCafeIcon],
  [/(^|\s)(juice|smoothie|shake|milkshake|soda|drink|beverage|soft)/i, LocalDrinkIcon],
  [/(^|\s)(cocktail|liquor|whiskey|vodka|tequila|rum|spirits?)/i, LiquorIcon],
  [/(^|\s)(beer|wine|alcohol|bar)/i, LocalBarIcon],
  [/(^|\s)(ice.?cream|gelato|sorbet|frozen)/i, IcecreamIcon],
  [/(^|\s)(cake|pastry|cheesecake|tiramisu|tart)/i, CakeIcon],
  [/(^|\s)(cookie|brownie|donut|doughnut)/i, CookieIcon],
  [/(^|\s)(breakfast|brunch|pancake|waffle|french.?toast)/i, BreakfastDiningIcon],
  [/(^|\s)(egg|omelet|omelette|frittata)/i, EggAltIcon],
  [/(^|\s)(appetizer|appetiser|starter|small.?plates?|tapas|antipasti|antipasto)/i, TapasIcon],
  [/(^|\s)(soup|stew|chowder|broth)/i, SoupKitchenIcon],
  [/(^|\s)(dessert|sweet|sweets)/i, CakeIcon],
  [/(^|\s)(snack|chips|fries|sides?)/i, EmojiFoodBeverageIcon],
];

function categoryIconFor(name) {
  if (!name) return RestaurantIcon;
  for (const [re, Icon] of CATEGORY_ICON_RULES) {
    if (re.test(name)) return Icon;
  }
  return RestaurantIcon; // generic fork & knife fallback
}

// Convert any casing to Title Case (e.g. "APPETIZERS" → "Appetizers", "pizza" → "Pizza")
const toTitleCase = (str) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

function ChipIcon({ url, isActive }) {
  if (!url) return null;
  // Inline SVG stored as text
  if (url.startsWith('<svg')) {
    return (
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', mr: 0.5, width: 20, height: 20, flexShrink: 0, filter: isActive ? 'brightness(0) invert(1)' : 'none' }}
        dangerouslySetInnerHTML={{ __html: url.replace(/<svg/, '<svg width="20" height="20"') }}
      />
    );
  }
  // URL (DALL-E image stored on volume)
  return (
    <Box
      component="img"
      src={resizedImage(url, 128)}
      alt=""
      sx={{ width: 44, height: 44, mr: 0.75, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, opacity: isActive ? 0.95 : 1 }}
    />
  );
}

// DoorDash-style vertical tile (icon stacked above small label). Used for
// the Categories row. Cuisines keep the inline pill style.
function CategoryTile({ title, iconUrl, isActive, onClick, theme }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        flexShrink: 0,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        width: 76,
        py: 0.5,
        userSelect: 'none',
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          backgroundColor: isActive ? theme.palette.primary.main : '#f3f4f6',
          border: isActive ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
          transition: 'all 0.15s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          '&:hover': {
            backgroundColor: isActive ? theme.palette.primary.main : '#e5e7eb',
          },
        }}
      >
        {iconUrl ? (
          <Box
            component="img"
            src={resizedImage(iconUrl, 128)}
            alt=""
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: isActive ? '#fff' : '#9ca3af' }}>
            {title === 'All' ? 'All' : title?.[0]?.toUpperCase()}
          </Typography>
        )}
      </Box>
      <Typography
        sx={{
          fontSize: '0.72rem',
          fontWeight: isActive ? 700 : 500,
          color: isActive ? theme.palette.primary.main : '#374151',
          textAlign: 'center',
          width: 76,
          lineHeight: 1.2,
          // Allow up to 2 lines, then clip with ellipsis on the 2nd.
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          wordBreak: 'break-word',
        }}
        title={title === 'All' ? title : toTitleCase(title)}
      >
        {title === 'All' ? title : toTitleCase(title)}
      </Typography>
    </Box>
  );
}

// The Catering entry (backend migration 131) — same footprint as a category
// tile, in the catering accent. Opens the Catering page.
function CateringTile({ onClick }) {
  return (
    <Box
      onClick={onClick}
      role="link"
      sx={{
        flexShrink: 0,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        width: 76,
        py: 0.5,
        userSelect: 'none',
        '&:hover > div': { borderColor: CATERING_COLOR },
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          backgroundColor: CATERING_SOFT,
          border: `2px solid ${CATERING_BORDER}`,
          transition: 'all 0.15s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <RoomServiceRoundedIcon sx={{ color: CATERING_COLOR, fontSize: 28 }} />
      </Box>
      <Typography
        sx={{
          fontSize: '0.72rem',
          fontWeight: 700,
          color: CATERING_COLOR,
          textAlign: 'center',
          width: 76,
          lineHeight: 1.2,
        }}
      >
        Catering
      </Typography>
    </Box>
  );
}

// Also the Catering page's cuisine + category row.
export function ChipRow({ label, items, selected, onSelect, theme, useStandardIcons, leading }) {
  if (items.length <= 1) return null;
  const isTileMode = !!useStandardIcons;
  return (
    <Box>
      {label && (
        <Typography variant="h6" sx={{ color: '#111827', fontWeight: 700, mb: 1.25, display: 'block', fontSize: '1.05rem' }}>
          {label}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          gap: isTileMode ? 0.5 : 1,
          overflowX: 'auto',
          pb: 0.5,
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': { borderRadius: 2, backgroundColor: 'rgba(0,0,0,0.15)' },
        }}
      >
        {leading}
        {items.map((item) => {
          const title = typeof item === 'string' ? item : (item.title || item.name);
          const iconUrl = typeof item === 'string' ? null : (item.icon || item.image || null);
          const isActive = selected === title;
          const handleClick = () => onSelect(isActive && title !== 'All' ? 'All' : title);

          if (isTileMode) {
            return (
              <CategoryTile
                key={title}
                title={title}
                iconUrl={iconUrl}
                isActive={isActive}
                onClick={handleClick}
                theme={theme}
              />
            );
          }

          return (
            <Chip
              key={title}
              label={
                <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                  <ChipIcon url={iconUrl} isActive={isActive} />
                  {title === 'All' ? title : toTitleCase(title)}
                </Box>
              }
              onClick={handleClick}
              variant={isActive ? 'filled' : 'outlined'}
              sx={{
                flexShrink: 0,
                fontWeight: isActive ? 700 : 500,
                backgroundColor: isActive ? theme.palette.primary.main : 'transparent',
                color: isActive ? '#fff' : '#333',
                borderColor: isActive ? theme.palette.primary.main : '#ddd',
                '&:hover': {
                  backgroundColor: isActive ? theme.palette.primary.dark : 'rgba(0,0,0,0.06)',
                },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

function RestaurantGrid(props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const extraSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const classes = useStyles(extraSmall);
  const [selectedCuisine, setSelectedCuisine] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Cuisines: from getCuisines query (with images), filtered to only those present in current restaurants
  const cuisines = useMemo(() => {
    const activeNames = new Set(props.restaurants.flatMap((r) => r.cuisines ?? []));
    const withImages = (props.cuisineData ?? []).filter((c) => activeNames.has(c.name));
    // Fall back to plain strings if cuisineData not loaded yet
    const fallback = [...activeNames].filter(Boolean).map((name) => ({ name, image: null }));
    const list = withImages.length > 0 ? withImages : fallback;
    return [{ name: 'All', image: null }, ...list];
  }, [props.restaurants, props.cuisineData]);

  // Categories come from the legacy Category table (carries icons). We also
  // fall back to advancedMenu items' categoryName for restaurants that only
  // use the new menu builder.
  const categories = useMemo(() => {
    const seen = new Set();
    // Catering categories are reached through the Catering tile instead.
    const cateringTitles = new Set(
      props.restaurants
        .flatMap((r) => r.categories ?? [])
        .filter((c) => c.isCatering && c.title)
        .map((c) => c.title.trim().toLowerCase())
    );
    const fromLegacy = props.restaurants
      .flatMap((r) => r.categories ?? [])
      .filter((c) => !c.isCatering);
    // The legacy Category table carries the generated icon. Advanced-menu
    // categories are just name strings, so look up their icon by title from
    // the legacy categories (case-insensitive) instead of hardcoding null.
    const iconByTitle = new Map();
    fromLegacy.forEach((c) => {
      if (c.title && c.icon) iconByTitle.set(c.title.trim().toLowerCase(), c.icon);
    });
    const fromAdvanced = props.restaurants.flatMap((r) =>
      (r.advancedMenus ?? []).flatMap((m) =>
        (m.items ?? [])
          .map((it) => it.categoryName)
          .filter((title) => title && !cateringTitles.has(title.trim().toLowerCase()))
          .map((title) => ({ title, icon: iconByTitle.get(title.trim().toLowerCase()) || null }))
      )
    );
    const all = [...fromLegacy, ...fromAdvanced];
    const unique = all.filter((c) => c.title && !seen.has(c.title) && seen.add(c.title));
    return [{ title: 'All', icon: null }, ...unique];
  }, [props.restaurants]);

  // Filter restaurants by selected cuisine + category (AND logic). A category
  // can come from either source, so check both.
  const filteredRestaurants = useMemo(() => {
    return props.restaurants.filter((r) => {
      const matchesCuisine =
        selectedCuisine === 'All' ||
        (r.cuisines ?? []).includes(selectedCuisine);
      const inLegacy = r.categories?.some((c) => c.title === selectedCategory);
      const inAdvanced = (r.advancedMenus ?? []).some((m) =>
        (m.items ?? []).some((it) => it.categoryName === selectedCategory)
      );
      const matchesCategory =
        selectedCategory === 'All' || inLegacy || inAdvanced;
      return matchesCuisine && matchesCategory;
    });
  }, [props.restaurants, selectedCuisine, selectedCategory]);

  const displayedRestaurants = props.search ? props.restaurants : filteredRestaurants;
  const showFilters = !props.search;
  // The Catering tile shows once a nearby restaurant offers catering.
  const anyCatering = useMemo(() => props.restaurants.some((r) => r.hasCatering), [props.restaurants]);
  const searchingCatering = queryMentionsCatering(props.search);

  return (
    <Box className={classes.mainContainer} sx={{ overflowX: "hidden" }}>
      <Box sx={{
        // Full-width (DoorDash-style): no maxWidth cap and no centering, so the
        // heading, category chips, and grid all span from the sidebar to the
        // right edge instead of stopping at 1400px and leaving an empty band.
        width: "100%",
        // box-sizing so the px padding doesn't push width > 100%.
        boxSizing: "border-box",
        px: { xs: 2, sm: 2, md: 3 },
      }}>
        <Box style={{ paddingTop: "16px", paddingBottom: "2rem" }}>
          <Title title={t('allRestaurant')} />

          {/* "See restaurants open now" from the closed-restaurant popup. */}
          {props.openNowOnly && (
            <Box
              component="button"
              type="button"
              onClick={props.onClearOpenNow}
              aria-label="Open now filter on. Show all restaurants"
              sx={{
                appearance: "none", border: 0, m: 0, mt: 1.5, font: "inherit", cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 0.75, height: 36, pl: 1.75, pr: 1.25,
                borderRadius: "999px", bgcolor: C.redTint, color: C.red, fontSize: 14, fontWeight: 700,
                boxShadow: `inset 0 0 0 1.5px ${C.red}`,
                "&:focus-visible": { outline: `2px solid ${C.ink}`, outlineOffset: "2px" },
              }}
            >
              <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: C.green }} />
              Open now
              <CloseRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
            </Box>
          )}

          {showFilters && (() => {
            // Combine cuisines + categories into a single horizontally
            // scrolling tile strip. Each item is tagged with `kind` so we
            // know which selector to update when tapped. Active tile is
            // whichever the user last picked from this row.
            const cuisineItems = cuisines
              .filter(c => c.name !== 'All')
              .map(c => ({ title: c.name, icon: c.image || null, kind: 'cuisine' }));
            const categoryItems = categories
              .filter(c => c.title !== 'All')
              .map(c => ({ title: c.title, icon: c.icon || null, kind: 'category' }));
            const combined = [
              { title: 'All', icon: null, kind: 'all' },
              ...cuisineItems,
              ...categoryItems,
            ];
            const currentSelection =
              selectedCuisine !== 'All' ? selectedCuisine
              : selectedCategory !== 'All' ? selectedCategory
              : 'All';
            const handleCombinedSelect = (titleClicked) => {
              const item = combined.find(i => i.title === titleClicked);
              if (!item || item.kind === 'all') {
                setSelectedCuisine('All');
                setSelectedCategory('All');
                return;
              }
              if (item.kind === 'cuisine') {
                setSelectedCategory('All');
                setSelectedCuisine(titleClicked === selectedCuisine ? 'All' : titleClicked);
              } else {
                setSelectedCuisine('All');
                setSelectedCategory(titleClicked === selectedCategory ? 'All' : titleClicked);
              }
            };
            return (
              <Box sx={{ mt: 2, mb: 2 }}>
                <ChipRow
                  label="What are you in the mood for?"
                  items={combined}
                  selected={currentSelection}
                  onSelect={handleCombinedSelect}
                  theme={theme}
                  useStandardIcons
                  leading={anyCatering ? <CateringTile onClick={() => navigate('/catering')} /> : null}
                />
              </Box>
            );
          })()}

          {/* A search for catering offers the Catering page. */}
          {searchingCatering && (
            <Box
              onClick={() => navigate('/catering')}
              role="link"
              sx={{
                mt: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                maxWidth: 520,
                px: 1.75,
                py: 1.25,
                borderRadius: '14px',
                bgcolor: CATERING_SOFT,
                border: `1px solid ${CATERING_BORDER}`,
                cursor: 'pointer',
                transition: 'border-color 0.15s',
                '&:hover': { borderColor: CATERING_COLOR },
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: CATERING_COLOR,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <RoomServiceRoundedIcon sx={{ color: '#fff', fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: CATERING_COLOR, lineHeight: 1.2 }}>
                  Catering
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>
                  See restaurants that cater near you
                </Typography>
              </Box>
              <ChevronRightRoundedIcon sx={{ color: CATERING_COLOR }} />
            </Box>
          )}

          <Box mt={showFilters ? 1 : 4} />
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {displayedRestaurants.length < 1 ? (
              <Grid container item xs={12} justifyContent="center">
                <Box display="flex" flexDirection="column" alignItems="center">
                  <EmptyIcon />
                  <Typography
                    variant="h5"
                    align="center"
                    color="textSecondary"
                    style={{ fontWeight: 800, marginTop: 15 }}
                  >
                    {props?.search
                      ? "That's not in the list"
                      : "No restaurants found"}{" "}
                  </Typography>
                  <Typography
                    variant="h5"
                    align="center"
                    color="textSecondary"
                    style={{ fontWeight: 800, marginTop: 15 }}
                  >
                    {props?.search && "yet"}
                  </Typography>
                  <Typography
                    variant="h6"
                    align="center"
                    color="textSecondary"
                    style={{ marginTop: 15 }}
                  >
                    {props?.search && "Try different search"}
                  </Typography>
                </Box>
              </Grid>
            ) : (
              displayedRestaurants.map((value, index) => (
                <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
                  <Card
                    data={value}
                    cardImageHeight="200px"
                    grid={true}
                    checkCart={props.checkCart}
                    showMessage={props.showMessage}
                    isSmall={false}
                  />
                </Grid>
              ))
            )}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}

export default React.memo(RestaurantGrid);
